export type MissionCategory =
  | "buyer"
  | "seller"
  | "messaging"
  | "profile"
  | "loop"
  | "bug_hunt";

export type MissionDetect =
  | { kind: "mutation"; method: string; urlPattern: RegExp }
  | { kind: "route"; pattern: RegExp }
  | { kind: "click"; selector: string }
  | { kind: "event"; name: string }
  | { kind: "manual" };

export type Mission = {
  id: string;
  category: MissionCategory;
  title: string;
  blurb: string;
  points: number;
  startRoute?: string;
  help?: string;
  detect: MissionDetect;
  prereq?: string[];
  realReward?: boolean;
};

export const MISSIONS: Mission[] = [
  // Buyer trail
  {
    id: "B01",
    category: "buyer",
    title: "Sort the home feed by distance",
    blurb: "Find the sort/filter control on the home page and choose Distance.",
    points: 5,
    startRoute: "/",
    help: "Tap the Sort chip on the home feed and pick Nearest.",
    detect: { kind: "event", name: "mission:sort-distance" },
  },
  {
    id: "B02",
    category: "buyer",
    title: "Filter listings by fulfillment method",
    blurb: "Try the fulfillment filter (delivery / pickup / community hub).",
    points: 5,
    startRoute: "/",
    detect: { kind: "event", name: "mission:filter-fulfillment" },
  },
  {
    id: "B05",
    category: "buyer",
    title: "Visit a demo seller's page",
    blurb: "Tap into any demo producer to see how their profile looks.",
    points: 5,
    startRoute: "/",
    detect: { kind: "route", pattern: /^\/u\// },
  },
  {
    id: "B06",
    category: "buyer",
    title: "Open a product detail page",
    blurb: "Tap into any listing to see the full detail page.",
    points: 5,
    startRoute: "/",
    detect: { kind: "route", pattern: /^\/items\// },
  },
  {
    id: "B07",
    category: "buyer",
    title: "Add a demo item to your cart",
    blurb: "Pick one item from a demo seller and tap Add to cart.",
    points: 10,
    startRoute: "/",
    help: "Tap Add to cart on any demo listing.",
    detect: { kind: "click", selector: '[data-mission-target="add-to-cart"]' },
  },
  {
    id: "B09",
    category: "buyer",
    title: "Pick a fulfillment option at checkout",
    blurb: "Choose how you want to receive your order at checkout.",
    points: 10,
    startRoute: "/checkout",
    detect: { kind: "event", name: "mission:checkout-fulfillment" },
  },
  {
    id: "B10",
    category: "buyer",
    title: "Complete a test checkout",
    blurb:
      "Use a Stripe test card (4242 4242 4242 4242) to place a real order against demo sellers.",
    points: 25,
    startRoute: "/checkout",
    detect: {
      kind: "mutation",
      method: "POST",
      urlPattern: /^\/api\/(checkout|orders)(\/|$)/,
    },
  },
  {
    id: "B11",
    category: "buyer",
    title: "Reach the order confirmation screen",
    blurb: "After checkout you should land on the confirmation page.",
    points: 5,
    detect: { kind: "route", pattern: /^\/order-confirmation/ },
  },
  {
    id: "B13",
    category: "buyer",
    title: "Message a seller from your order",
    blurb: "Send a quick note to your demo seller from the order chat.",
    points: 10,
    startRoute: "/orders",
    detect: {
      kind: "mutation",
      method: "POST",
      urlPattern: /^\/api\/orders\/[^/]+\/messages$/,
    },
  },
  {
    id: "B15",
    category: "buyer",
    title: "Leave a review",
    blurb: "Once your order is delivered, leave a star rating and a short note.",
    points: 15,
    detect: { kind: "mutation", method: "POST", urlPattern: /^\/api\/reviews(\/|$)/ },
  },

  // Seller trail
  {
    id: "S01",
    category: "seller",
    title: "Switch to seller mode",
    blurb: "Open the role toggle in your account settings and pick seller (or both).",
    points: 5,
    startRoute: "/account",
    detect: { kind: "manual" },
  },
  // S02 (separate "take/upload a photo" mission) merged into S05 — the
  // photo step is already part of publishing a listing, so the standalone
  // mission was redundant + made testers feel double-counted.
  {
    id: "S05",
    category: "seller",
    title: "Publish your first listing",
    blurb:
      "Open AI Quick Listing, capture or upload a product photo, fill in the details, and publish.",
    points: 25,
    startRoute: "/seller?action=add",
    help: "From the bottom nav, tap the + button to launch AI Quick Listing. Take a photo of your product or pick one from your gallery, then fill in the details and tap Publish.",
    detect: { kind: "mutation", method: "POST", urlPattern: /^\/api\/items(\/|$)/ },
  },
  {
    id: "S06",
    category: "seller",
    title: "Edit a published listing",
    blurb: "Open one of your listings and update something — price, description, anything.",
    points: 10,
    startRoute: "/seller",
    detect: { kind: "mutation", method: "PUT", urlPattern: /^\/api\/items\/[^/]+$/ },
  },
  {
    id: "S08",
    category: "seller",
    title: "Update an order's status",
    blurb: "Mark an order ready, out for delivery, or any other status change.",
    points: 10,
    startRoute: "/seller",
    detect: { kind: "mutation", method: "POST", urlPattern: /^\/api\/orders\/[^/]+\/status$/ },
  },

  // Messaging
  {
    id: "M01",
    category: "messaging",
    title: "Open the Messages page",
    blurb: "Find the Messages tab and open at least one thread.",
    points: 5,
    startRoute: "/messages",
    detect: { kind: "route", pattern: /^\/messages/ },
  },

  // Profile / Account
  {
    id: "P02",
    category: "profile",
    title: "Toggle your role to Both",
    blurb: "Switch your account so you're a buyer and seller.",
    points: 5,
    startRoute: "/account",
    detect: { kind: "manual" },
  },
  {
    id: "P03",
    category: "profile",
    title: "Enable push notifications",
    blurb: "Grant the notifications permission so you get order alerts.",
    points: 10,
    // No startRoute — push permission is system-level (granted at app
    // boot via the OS prompt, or in your phone's Settings). Auto-
    // detected when the device token POSTs successfully.
    help: "If your phone didn't prompt at first launch, open your phone's Settings → Stoop → Notifications and toggle them on. The mission completes once the app registers your device.",
    detect: {
      kind: "mutation",
      method: "POST",
      urlPattern: /^\/api\/my\/device-tokens(\/|$)/,
    },
  },

  // Full loop (the standout)
  {
    id: "L01",
    category: "loop",
    title: "A demo buyer placed an order on your listing",
    blurb: "Once your listing is live, a demo buyer will purchase it within a few minutes. This unlocks once that order arrives in /seller.",
    points: 25,
    startRoute: "/seller",
    detect: { kind: "manual" },
    prereq: ["S05"],
  },
  {
    id: "L02",
    category: "loop",
    title: "Reply to the demo buyer's first message",
    blurb: "Open the order chat and answer the buyer's question.",
    points: 15,
    startRoute: "/seller",
    detect: {
      kind: "mutation",
      method: "POST",
      urlPattern: /^\/api\/orders\/[^/]+\/messages$/,
    },
    prereq: ["L01"],
  },
  {
    id: "L03",
    category: "loop",
    title: "Mark the order ready for handoff",
    blurb: "Use the seller dashboard to advance the order status.",
    points: 15,
    startRoute: "/seller",
    detect: { kind: "mutation", method: "POST", urlPattern: /^\/api\/orders\/[^/]+\/status$/ },
    prereq: ["L02"],
  },
  {
    id: "L04",
    category: "loop",
    title: "Drive a real delivery end-to-end",
    blurb:
      "Accept a delivery, drive to pickup, drop it off. The standout reward — finishing this earns a real-world thank-you.",
    points: 100,
    startRoute: "/deliveries",
    detect: {
      kind: "mutation",
      method: "POST",
      urlPattern: /^\/api\/delivery-requests\/[^/]+\/deliver$/,
    },
    realReward: true,
  },
];

export const CATEGORY_LABELS: Record<MissionCategory, string> = {
  buyer: "Buyer trail",
  seller: "Seller trail",
  messaging: "Messaging",
  profile: "Profile",
  loop: "Full loop",
  bug_hunt: "Bug hunts",
};

export function levelForPoints(totalPoints: number): number {
  if (totalPoints <= 0) return 0;
  return Math.floor(Math.sqrt(totalPoints / 10));
}

export function pointsForNextLevel(level: number): number {
  return Math.pow(level + 1, 2) * 10;
}
